import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import Link from 'next/link'
import { cn } from './cn'

/**
 * Carte.
 *
 * La marque est plate : aucune ombre portée. L'élévation se lit uniquement au
 * filet (`hairline`), et l'état se lit à la couleur de la bordure — lavande pour
 * une carte sélectionnée ou mise en avant, corail pour une carte en alerte.
 *
 * `tone="ink"` correspond aux bandeaux sombres des maquettes (la carte « Reprendre
 * la lecture » de l'accueil apprenant, par exemple).
 */

type Tone = 'surface' | 'cream' | 'ink'
type State = 'default' | 'selected' | 'alert'

const TONES: Record<Tone, string> = {
  surface: 'bg-surface text-ink',
  cream: 'bg-cream-warm text-ink',
  ink: 'bg-ink text-cream',
}

const STATES: Record<State, string> = {
  default: 'border-hairline',
  selected: 'border-[1.5px] border-lavender',
  alert: 'border-[1.5px] border-alert-border',
}

type CardStyle = {
  tone?: Tone
  state?: State
  /** 24px pour une carte principale, 20px pour une carte secondaire. */
  size?: 'md' | 'sm'
}

type CardProps = CardStyle & {
  children: ReactNode
  className?: string
}

function cardClasses({ tone = 'surface', state = 'default', size = 'md' }: CardStyle) {
  return cn(
    'border',
    size === 'md' ? 'rounded-card p-5' : 'rounded-card-sm p-4',
    TONES[tone],
    STATES[state],
  )
}

export function Card({ children, className, ...style }: CardProps) {
  return <div className={cn(cardClasses(style), className)}>{children}</div>
}

/**
 * Carte cliquable. Le survol passe la bordure en lavande — jamais de fondu
 * d'opacité, jamais de déplacement.
 */
export function CardLink({
  children,
  className,
  tone,
  state,
  size,
  ...rest
}: CardProps & ComponentPropsWithoutRef<typeof Link>) {
  return (
    <Link
      className={cn(cardClasses({ tone, state, size }), 'block hover:border-lavender', className)}
      {...rest}
    >
      {children}
    </Link>
  )
}
